import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  Braces,
  CircleHelp,
  Eye,
  FlaskConical,
  Link as LinkIcon,
  Mail,
  MessageCircle,
  LucideIcon,
} from 'lucide-react';
import { AssistantProviderIcon } from './AssistantProviderIcon';
import { isPublicAssistantSource } from './assistantSource';
import { AssistantSourceDto } from '@/Domain/api/Conversation/dto/AssistantSource.dto';
import { ConversationSourceEnum } from '@/Domain/enum/ConversationSource.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';

type Props = {
  source: ConversationSourceEnum;
  assistantSource?: AssistantSourceDto | null;
  colorIconsType?: 'inherit' | 'config';
  fontAwesomeProps?: Partial<FontAwesomeIconProps>;
};

export const ConversationSourceIcon = ({
  source,
  assistantSource,
  fontAwesomeProps,
  colorIconsType = 'config',
}: Props) => {
  const applyConfigColors = colorIconsType === 'config';
  const { fixedWidth = true, ...restFontAwesomeProps } = fontAwesomeProps || {};
  const iconClassName = classNames('h-3.5 w-3.5 shrink-0 transition-all ease-in duration-300', {
    [`${ConversationHelper.getConversationIconColor(source)}`]: applyConfigColors,
    'scale-[0.78]': source === ConversationSourceEnum.ZENDESK,
  });
  const lucideSources: Partial<Record<ConversationSourceEnum, LucideIcon>> = {
    [ConversationSourceEnum.WIDGET]: MessageCircle,
    [ConversationSourceEnum.API]: Braces,
    [ConversationSourceEnum.PREVIEW]: Eye,
    [ConversationSourceEnum.PUBLIC__LINK]: LinkIcon,
    [ConversationSourceEnum.SIMULATION]: FlaskConical,
    [ConversationSourceEnum.EMAIL]: Mail,
  };

  if (isPublicAssistantSource(source)) {
    return <AssistantProviderIcon source={source} evidence={assistantSource} />;
  }

  if (ConversationHelper.isCustomConversationSource(source)) {
    const Icon = ConversationHelper.getCustomConversationIcon(source);

    return <Icon className={iconClassName} data-testid="conversation-source-icon" />;
  }

  const LucideSourceIcon = lucideSources[source];
  if (LucideSourceIcon) {
    return <LucideSourceIcon className={iconClassName} data-testid="conversation-source-icon" />;
  }

  // Backend owns the source enum and can ship a value this build doesn't map.
  const fontAwesomeIcon = ConversationHelper.getConversationIcon(source);
  if (!fontAwesomeIcon) {
    return <CircleHelp className={iconClassName} data-testid="conversation-source-icon" />;
  }

  return (
    <FontAwesomeIcon
      icon={fontAwesomeIcon}
      className={iconClassName}
      fixedWidth={fixedWidth}
      {...restFontAwesomeProps}
      data-testid="conversation-source-icon"
    />
  );
};
